<template>
  <div class="content-container">
    <div class="header-fill"></div>

    <div class="content-nav-bar">
      <template v-if="this.currentSubmenu">
        <div class="left">
          <a href="#" @click.prevent="locateTo(menuId)"><i class="el-icon-arrow-left"></i>返回</a>
          <span style="margin-left: 40px;">{{this.currentSubmenu.title}}</span>
        </div>
        <div class="right">
          <a href="#" :disabled="isFirstSubmenu" @click.prevent="handleGoContent(-1)">上一条</a>
          <a href="#" :disabled="isLastSubmenu" @click.prevent="handleGoContent(1)">下一条</a>
        </div>
      </template>
    </div>

    <div class="content" v-loading="loading">
      <section class="left-section">
        <ul class="menu-list">
          <li
            v-for="item in menuList"
            class="menu-item"
            :class="{ active: item.id === menuId }"
            :key="item.id">
            <a href="#" @click.prevent="handleClick(item)">{{item.title}}</a>
          </li>
        </ul>
      </section>

      <section class="right-section" v-loading="subLoading">
        <div v-if="isEmpty" v-show="!subLoading" class="empty-content">暂无内容</div>

        <template v-else>
          <ul v-if="showSubmenu" class="sub-menu-list">
            <li
              v-for="item in currentMenu.submenu"
              class="sub-menu-item"
              :class="{ active: item.id === submenuId }"
              :key="item.id">
              <a href="#" @click.prevent="handleClick(item)">{{item.title}}</a>
            </li>
          </ul>

          <div
            v-else
            v-show="!subLoading"
            class="content-wrapper"
            v-html="content">
          </div>
        </template>
      </section>
    </div>
  </div>
</template>

<script>
import markdown from 'markdown-it';

const md = markdown({
  html: true,
  breaks: true,
});

export default {
  data() {
    return {
      loading: false,
      subLoading: false,
      menuList: [],
      content: '',
      menuId: null,
      submenuId: null,
    };
  },
  computed: {
    currentMenu() {
      return this.menuList.find(item => item.id === this.menuId) || {};
    },
    currentSubmenu() {
      if (this.submenuId && Array.isArray(this.currentMenu.submenu)) {
        return this.currentMenu.submenu.find(item => item.id === this.submenuId) || null;
      }
      return null;
    },
    isEmpty() {
      if (this.showSubmenu && this.currentMenu.submenu.length === 0) {
        return true;
      }
      if (!this.showSubmenu && !this.content) {
        return true;
      }
      return false;
    },
    isFirstSubmenu() {
      if (Array.isArray(this.currentMenu.submenu)) {
        const index = this.currentMenu.submenu.indexOf(this.currentSubmenu);
        return index === 0;
      }
      return false;
    },
    isLastSubmenu() {
      if (Array.isArray(this.currentMenu.submenu)) {
        const index = this.currentMenu.submenu.indexOf(this.currentSubmenu);
        return index === this.currentMenu.submenu.length - 1;
      }
      return false;
    },
    showSubmenu() {
      return this.currentMenu.hasSubmenu
        && Array.isArray(this.currentMenu.submenu)
        && !this.submenuId;
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler(val) {
        const { menuId, submenuId } = val.params;
        if (menuId && !isNaN(menuId)) {
          this.menuId = Number(menuId);
        } else {
          this.menuId = null;
        }
        if (submenuId && !isNaN(submenuId)) {
          this.submenuId = Number(submenuId);
        } else {
          this.submenuId = null;
        }

        if (this.menuReady) {
          this.fetchAsNeed();
        }
      },
    },
  },
  created() {
    this.fetchMenu();
  },
  methods: {
    async fetchMenu() {
      this.loading = true;

      try {
        const { data } = await this.$http.get('/hostweb/menus');
        this.menuList = data.map((item) => {
          item.hasSubmenu = item.type === 0;
          item.submenu = null;
          item.renderContent = null;
          return item;
        });

        this.menuReady = true;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
      await this.fetchAsNeed();
    },
    async fetchSubmenu(menu) {
      const { data } = await this.$http.get('/hostweb/list', {
        params: {
          menuId: menu.id,
          page: 0,
          pageSize: 1000,
        },
      });
      menu.submenu = data.map((item) => {
        item.content = item.content || '';
        item.renderContent = md.render(item.content);
        item.parent = menu;
        return item;
      });
    },
    async fetchContent(menu) {
      const { data } = await this.$http.get('/hostweb/list', {
        params: {
          menuId: menu.id,
          page: 0,
          pageSize: 1000,
        },
      });
      const target = data.find(item => item.menuId === menu.id) || {};
      menu.content = target.content || '';
      menu.renderContent = md.render(menu.content);
    },
    async fetchAsNeed() {
      if (this.menuList.length === 0) return;
      this.content = '';

      try {
        this.subLoading = true;
        let dirty = false;

        if (!this.menuId) {
          dirty = true;
          this.menuId = this.menuList[0].id;
        }
        if (this.currentMenu.hasSubmenu) {
          if (!Array.isArray(this.currentMenu.submenu)) {
            await this.fetchSubmenu(this.currentMenu);
          }
          if (this.submenuId && this.currentMenu.submenu) {
            const submenu = this.currentMenu.submenu.find(item => item.id === this.submenuId);
            if (submenu) {
              if (!submenu.renderContent) {
                await this.fetchContent(submenu);
              }
              this.content = submenu.renderContent;
            }
          }
        } else {
          if (!this.currentMenu.renderContent) {
            await this.fetchContent(this.currentMenu);
          }
          this.content = this.currentMenu.renderContent;
        }

        if (dirty) {
          this.locateTo(this.menuId, this.submenuId);
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.subLoading = false;
      }
    },
    handleClick(item) {
      let menuId = item.id;
      let submenuId;
      if (item.parent) {
        submenuId = menuId;
        menuId = item.parent.id;
      }
      this.locateTo(menuId, submenuId);
    },
    handleGoContent(n) {
      if (Array.isArray(this.currentMenu.submenu)) {
        const index = this.currentMenu.submenu.indexOf(this.currentSubmenu);
        const target = this.currentMenu.submenu[index + n];
        if (target) {
          this.locateTo(this.menuId, target.id);
        }
      }
    },
    locateTo(menuId, submenuId) {
      let path = '/content';
      if (menuId) {
        path += `/${menuId}`;
        if (submenuId) {
          path += `/${submenuId}`;
        }
      }
      if (this.$route.path !== path) {
        this.$router.push(path);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  $left-width: 200px;
  $link-color: #1846fe;

  .content-container {
    .header-fill {
      background: #0B0B0C url("./images/background.png") center no-repeat;
      height: 186px;
    }

    > .content-nav-bar {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      height: 80px;
      width: $content-width;
      border-bottom: 1px solid #e9e9e9;
      padding-left: $left-width;
      padding-bottom: 20px;
      box-sizing: border-box;
      margin: 0 auto;
      font-size: 16px;
      color: #333333;

      a {
        cursor: pointer;
        color: inherit;
        text-decoration: none;

        &:hover {
          color: $link-color;
        }

        & + a {
          margin-left: 10px;
        }

        &[disabled] {
          color: #999999;
          pointer-events: none;
          cursor: default;
        }
      }

      > .left {
      }

      > .right {
        margin-right: 100px;
      }
    }

    > .content {
      width: $content-width;
      margin: 0 auto;
      min-height: 300px;
      display: flex;

      > .left-section {
        box-sizing: border-box;
        width: $left-width;
        border-right: 1px solid #e9e9e9;

        .menu-list {
          list-style: none;
          padding-top: 6px;
        }

        .menu-item {
          margin-top: 32px;
          font-size: 18px;
          font-weight: 200;
          color: #333333;
          transition: none;
          text-align: right;
          padding-right: 30px;
          box-sizing: border-box;

          &.active {
            color: $link-color;
            font-weight: 400;
          }

          &:hover {
            color: $link-color;
          }

          a {
            text-decoration: none;
            color: inherit;
          }
        }
      }

      > .right-section {
        flex: 1;

        .empty-content {
          padding: 40px;
          color: #999999;
          font-size: 16px;
          font-weight: 200;
          transition: none;
        }

        .sub-menu-list {
          list-style: none;
          box-sizing: border-box;
          padding-top: 38px;
          padding-left: 40px;
        }

        .sub-menu-item {
          margin-bottom: 24px;
          color: #333333;
          transition: none;
          font-size: 18px;
          font-weight: 200;

          &:hover {
            color: $link-color;
          }

          a {
            text-decoration: underline;
            color: inherit;
          }
        }

        .content-wrapper {
          box-sizing: border-box;
          padding: 40px;
          font-size: 16px;
          font-weight: 200;
          color: #333333;
          line-height: 1.5;
          word-break: break-word;
          transition: none;

          /deep/ {
            a {
              color: #333333;
              text-decoration: underline;

              &:hover {
                color: $link-color;
              }
            }

            b {
              font-weight: 400;
            }

            img,
            video {
              margin: 20px 10px 20px 0;
              max-width: 100%;
            }
          }
        }
      }
    }
  }
</style>
